import React from 'react';
import './index.css';

type RadioButtonProps = {
  value: string;
  children: React.ReactNode;
  name?: string;
  disabled?: boolean;
};

type InjectedProps = {
  selected?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  onChange?: (value: string) => void;
};

const RadioButton = ({
  value,
  children,
  name,
  disabled = false,
  selected = false,
  isFirst = false,
  isLast = false,
  onChange
}: RadioButtonProps & InjectedProps) => {
  const classes = [
    'cwf-kit-radio-button',
    selected ? 'cwf-kit-radio-button--selected' : '',
    isFirst ? 'cwf-kit-radio-button--first' : '',
    isLast ? 'cwf-kit-radio-button--last' : ''
  ].join(' ');

  return (
    <button
      type="button"
      name={name}
      value={value}
      disabled={disabled}
      aria-pressed={selected}
      className={classes}
      // Fires for the selected button too, as grow-ui's did.
      onClick={() => onChange?.(value)}
    >
      {children}
    </button>
  );
};

type UiKitRadioButtonGroupProps = {
  selected: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
};

/**
 * A row of joined toggle buttons with one selected, matching the grow-ui
 * RadioButtonGroup the Property view switcher was built with. The UI Kit's
 * Radio.Button looks and behaves differently (it ignores clicks on the
 * selected option), so this keeps the original.
 */
const UiKitRadioButtonGroup = ({
  selected,
  onChange,
  children
}: UiKitRadioButtonGroupProps) => {
  const buttons = React.Children.toArray(
    children
  ).filter((child): child is React.ReactElement<
    RadioButtonProps & InjectedProps
  > => React.isValidElement(child));

  return (
    <div className="cwf-kit-radio-button-group">
      {buttons.map((button, index) =>
        React.cloneElement(button, {
          selected: button.props.value === selected,
          isFirst: index === 0,
          isLast: index === buttons.length - 1,
          onChange
        })
      )}
    </div>
  );
};

UiKitRadioButtonGroup.RadioButton = RadioButton;

export default UiKitRadioButtonGroup;
